<template>
  <div
    :class="['b-inifinite-scroll', $style.scrollable]"
    ref="scrollable"
    @scroll='handleScroll'
  >
    <div :class="$style.content">
      <slot></slot>

      <!--
        Detector appearance on screen will trigger 'fetch-items'.
        It overlaps with list items but has 'z-index=-1'.
      -->
      <div :class="$style.detectorWrapper">
        <div
          :class="$style.detector"
          :style="{ height: loadMoreDelta + 'px' }"
          ref="detector"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { DEFAULT_LOAD_MORE_DELTA } from '@/lib/consts.js'

export default {

  props: {
    /* Distance to bottom at which fetch-items fires. */
    loadMoreDelta: {
      type: Number,
      default: DEFAULT_LOAD_MORE_DELTA,
    },
    /*
      Disallow automatic attempts to prefill the scrollable container.

      BEWARE: if container will not be filled to 100% by your first manual fetch or by
      prefilling routine, then (obviously) component won't be able to issue fetch-more events on scroll.

      If opting-out of prefill, make sure to have sane defaults (perPage relative to item and container sizes),
      or provide sufficient information to this component in the form of:
        'fetchInProgress', 'resourceDepleted', and by ticking ON 'uncapPrefillAttempts'
      so that prefill will always succeed.
    */
    noPrefill: {
      type: Boolean,
    },
    /*
      Use if you trigger first fetch manually in the parent and want to
      lower the likelihood of unwanted fetch of 2nd page right away.
    */
    defferFirstLoad: {
      type: Boolean,
    },
    defferedLoadDelay: {
      type: Number,
      default: 2, // [s]
    },
    /* Provide to prevent premature fetches. */
    fetchInProgress: {
      type: Boolean,
    },
    /* Provide to disregard max prefill attempts and retry so long the container has free space. */
    uncapPrefillAttempts: {
      type: Boolean,
    },
    /* Provide to prevent unnecessary fetch attempts. */
    resourceDepleted: {
      type: Boolean,
    },
  },

  data () {
    return {
      observer: null,
      prefillAttempts: 0,
      maxPrefillAttempts: 5,
      prefillInterval: 2, // [s]
      prefillTimeoutId: 0,
    }
  },

  computed: {
    validPrefillCount () {
      return (this.prefillAttempts < this.maxPrefillAttempts) || this.uncapPrefillAttempts
    },
  },

  methods: {
    delay (handler, delay = 2) {
      return setTimeout(handler, delay * 1000)
    },
    handleScroll (e) {
      this.scrollTop = e?.target?.scrollTop
    },
    /* Handler for observer responsible for loading elements on scroll to bottom. */
    loadMoreObserverHandler ([entry]) {
      if (entry.isIntersecting) this.$emit('fetch-items')
    },
    /*
      Handler for observer responsible for initial prefilling containing element
      regardless of it's height and number of elements loaded per page.

      It is "best effort" only.
      Prefill will fail in 2 cases:
      1) When there is not enough elements to fill the list
      2) When max number of attempts is not enough to fill the list
    */
    prefillObserverHandler ([entry]) {
      if (this.prefillTimeoutId) {
        clearTimeout(this.prefillTimeoutId)
        this.prefillTimeoutId = 0
      }

      if (!entry.isIntersecting || !this.validPrefillCount || this.resourceDepleted || this.noPrefill) {
        /* End prefill */
        this.attachObserver(this.loadMoreObserverHandler)
        return
      } else if (!this.fetchInProgress) {
        /* Prefill */
        this.$emit('fetch-items')
        this.prefillAttempts += 1
      }

      this.retryPrefill()
    },
    /*
      Manually schedule next handler run in case current fetch will not manage to fill the list.
      If it will - handler will be called anyway by intersection observer itself* and
      retyPrefill will be discarded (clearTimeout).

      * - because 'detector' element will be pushed out of 'scrollable'.
    */
    retryPrefill () {
      this.prefillTimeoutId = this.delay(
        () => this.prefillObserverHandler([{ isIntersecting: true }]),
        this.prefillInterval
      )
    },
    attachObserver (callback) {
      const options = {
        root: this.$refs.scrollable,
        rootMargin: '0px',
        threshold: 0,
      }

      this.disconnetCurrentObserver()

      this.observer = new IntersectionObserver(callback, options)
      if (this.$refs.detector) this.observer.observe(this.$refs.detector)
    },
    disconnetCurrentObserver () {
      if (this.observer) this.observer.disconnect()
    },
  },

  mounted () {
    /*
      Observer creation is delayed to give chance external fetch to pass.

      Why wait here instead of observer handler? Because we want current entries
      which observer will generate after it's attached.
    */
    this.$nextTick(() => {
      const delay = this.defferFirstLoad ? this.defferedLoadDelay : 0
      this.delay(this.attachObserver(this.prefillObserverHandler), delay)
    })
  },

  beforeUnmount () {
    this.disconnetCurrentObserver()
  },

  // keep-alive: https://vuejs.org/guide/built-ins/keep-alive.html#max-cached-instances
  activated () {
    if (this.$refs?.scrollable) {
      this.$refs.scrollable.scrollTop = this.scrollTop
    }
  },
}
</script>

<style lang="scss" module>
  .scrollable {
    position: relative;
    min-height: calc(100% - 78px);
    overflow-y: visible;

    @include media-breakpoint-desktop {
      overflow-y: auto;
    }
  }
  .content {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
  .detectorWrapper {
    position: relative;
    z-index: -1;
    pointer-events: none;
  }
  .detector {
    width: 100%;
    position: absolute;
    bottom: 0;
  }
</style>
